import { AccountCircle, Save } from '@mui/icons-material';
import { Box, Button, MenuItem } from '@mui/material';
import { useFormik, type FormikErrors } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateUser } from '~/api/users';
import { PasswordStrengthMeterBox } from '~/components/PasswordStrengthMeterBox';
import {
  DetailPasswordField,
  DetailSelect,
  DetailSwitch,
  DetailTextField,
} from '~/components/forms/details';
import { LanguageSelect } from '~/components/inputs';
import { PageContainer } from '~/components/page-layout';
import {
  SettingDescription,
  SettingInputWrapper,
  SettingLabelWrapper,
  SettingName,
  SettingsBody,
  SettingsData,
  SettingsHead,
} from '~/components/settings';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { MIN_PASSWORD_STRENGTH, usePasswordStrength } from '~/lib/password';

const FORM_ID = 'user-profile-form';

export const UserProfile = () => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();

  const [save] = useUpdateUser();

  const initialValues = useMemo(
    () => ({
      autoRefresh: currentUser.autoRefresh,
      defaultNetworkId: currentUser.defaultNetworkId ?? '',
      devicePreviewAutoplay: currentUser.devicePreviewAutoplay ?? true,
      hideDashboard: currentUser.hideDashboard,
      locale: currentUser.locale ?? '',
      name: currentUser.name,
      newPassword: '',
      newPasswordConfirmation: '',
      oldPassword: '',
    }),
    [currentUser],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (
      {
        autoRefresh,
        defaultNetworkId,
        devicePreviewAutoplay,
        hideDashboard,
        locale,
        name,
        newPassword,
        oldPassword,
      },
      { resetForm },
    ) => {
      const response = await save({
        variables: {
          userId: currentUser.id,
          patch: {
            autoRefresh,
            devicePreviewAutoplay,
            hideDashboard,
            locale: locale || null,
            name: name.trim(),
            newPassword,
            oldPassword,
            ...(currentUser.admin && {
              defaultNetworkId: defaultNetworkId === '' ? null : Number(defaultNetworkId),
            }),
          },
        },
      });
      resetForm();
      if (response.data && newPassword) location.reload();
    },
    validate: ({ name, newPassword, newPasswordConfirmation, oldPassword }) => {
      const errors: FormikErrors<typeof initialValues> = {};

      if (!name.trim()) {
        errors.name = t('pages.user.profile.fields.name.required');
      }

      if (newPassword || newPasswordConfirmation || oldPassword) {
        if (!newPassword) errors.newPassword = t('pages.user.profile.fields.new_password.required');
        if (!newPasswordConfirmation)
          errors.newPasswordConfirmation = t('pages.user.profile.fields.verify_password.required');
        if (!oldPassword)
          errors.oldPassword = t('pages.user.profile.fields.current_password.required');
      }

      if (oldPassword && newPassword && oldPassword === newPassword) {
        errors.newPassword = t('pages.user.profile.fields.new_password.must_differ');
      }

      if (newPassword && newPasswordConfirmation && newPassword !== newPasswordConfirmation) {
        errors.newPasswordConfirmation = t('pages.user.profile.fields.verify_password.must_match');
      }

      return errors;
    },
  });

  const passwordStrength = usePasswordStrength(formik.values.newPassword, [
    currentUser.email,
    currentUser.name,
    formik.values.name,
  ]);

  const actions = (
    <Button
      disabled={
        !formik.dirty ||
        !formik.isValid ||
        formik.isSubmitting ||
        (!!formik.values.newPassword && (passwordStrength?.score ?? 0) < MIN_PASSWORD_STRENGTH)
      }
      form={FORM_ID}
      startIcon={<Save />}
      type="submit"
      variant="contained"
    >
      {t('pages.user.profile.save')}
    </Button>
  );

  return (
    <>
      <Toolbar
        actions={actions}
        titleIcon={<AccountCircle />}
        titleText={t('pages.user.profile.title')}
      />

      <PageContainer>
        <Box
          component="form"
          id={FORM_ID}
          onSubmit={formik.handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Box>
            <SettingsHead>{t('pages.user.profile.sections.general')}</SettingsHead>
            <SettingsBody>
              <SettingsData>
                <SettingLabelWrapper>
                  <SettingName>{t('pages.user.profile.fields.name.label')}</SettingName>
                  <SettingDescription>
                    {t('pages.user.profile.fields.name.description')}
                  </SettingDescription>
                </SettingLabelWrapper>
                <SettingInputWrapper>
                  <DetailTextField
                    disabled={formik.isSubmitting}
                    error={!!formik.errors.name}
                    helperText={formik.errors.name}
                    autoComplete="name"
                    margin="dense"
                    name="name"
                    onChange={formik.handleChange}
                    value={formik.values.name}
                  />
                </SettingInputWrapper>
              </SettingsData>

              <SettingsData className="last">
                <SettingLabelWrapper>
                  <SettingName>{t('pages.user.profile.fields.email.label')}</SettingName>
                  <SettingDescription>
                    {t('pages.user.profile.fields.email.description')}
                  </SettingDescription>
                </SettingLabelWrapper>
                <SettingInputWrapper>
                  <DetailTextField
                    autoComplete="email"
                    disabled
                    margin="dense"
                    name="email"
                    value={currentUser.email}
                  />
                </SettingInputWrapper>
              </SettingsData>
            </SettingsBody>
          </Box>

          <Box>
            <SettingsHead>{t('pages.user.profile.sections.preferences')}</SettingsHead>
            <SettingsBody>
              {currentNetwork.settings.enableLanguageSelection && (
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('pages.user.profile.fields.language.label')}</SettingName>
                    <SettingDescription>
                      {t('pages.user.profile.fields.language.description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <LanguageSelect
                      disabled={formik.isSubmitting}
                      name="locale"
                      onChange={formik.handleChange}
                      value={formik.values.locale}
                    />
                  </SettingInputWrapper>
                </SettingsData>
              )}
              <SettingsData>
                <SettingLabelWrapper>
                  <SettingName>
                    {t('pages.user.profile.fields.device_preview_autoplay.label')}
                  </SettingName>
                  <SettingDescription>
                    {t('pages.user.profile.fields.device_preview_autoplay.description')}
                  </SettingDescription>
                </SettingLabelWrapper>
                <SettingInputWrapper>
                  <DetailSwitch
                    checked={formik.values.devicePreviewAutoplay}
                    color="primary"
                    disabled={formik.isSubmitting}
                    name="devicePreviewAutoplay"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                </SettingInputWrapper>
              </SettingsData>
              <SettingsData>
                <SettingLabelWrapper>
                  <SettingName>{t('pages.user.profile.fields.auto_refresh.label')}</SettingName>
                  <SettingDescription>
                    {t('pages.user.profile.fields.auto_refresh.description')}
                  </SettingDescription>
                </SettingLabelWrapper>
                <SettingInputWrapper>
                  <DetailSwitch
                    checked={formik.values.autoRefresh}
                    color="primary"
                    disabled={formik.isSubmitting}
                    name="autoRefresh"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                </SettingInputWrapper>
              </SettingsData>
              <SettingsData className={currentUser.admin ? undefined : 'last'}>
                <SettingLabelWrapper>
                  <SettingName>{t('pages.user.profile.fields.hide_dashboard.label')}</SettingName>
                  <SettingDescription>
                    {t('pages.user.profile.fields.hide_dashboard.description')}
                  </SettingDescription>
                </SettingLabelWrapper>
                <SettingInputWrapper>
                  <DetailSwitch
                    checked={formik.values.hideDashboard}
                    color="primary"
                    disabled={formik.isSubmitting}
                    name="hideDashboard"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                </SettingInputWrapper>
              </SettingsData>
              {currentUser.admin && (
                <SettingsData className="last">
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('pages.user.profile.fields.default_network.label')}
                    </SettingName>
                    <SettingDescription>
                      {t('pages.user.profile.fields.default_network.description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailSelect
                      disabled={formik.isSubmitting}
                      displayEmpty
                      name="defaultNetworkId"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.defaultNetworkId}
                    >
                      <MenuItem value="">
                        {t('pages.user.profile.fields.default_network.none')}
                      </MenuItem>
                      {currentUser.networks.map((network) => (
                        <MenuItem key={network.id} value={network.id}>
                          {network.name}
                        </MenuItem>
                      ))}
                    </DetailSelect>
                  </SettingInputWrapper>
                </SettingsData>
              )}
            </SettingsBody>
          </Box>

          <Box>
            <SettingsHead>{t('pages.user.profile.sections.security')}</SettingsHead>
            <SettingsBody>
              <SettingsData
                className={
                  !currentUser.authStrategy || currentUser.authStrategy === 'FANCONNECT'
                    ? undefined
                    : 'last'
                }
              >
                <SettingLabelWrapper>
                  <SettingName>{t('pages.user.profile.fields.auth_type.label')}</SettingName>
                  <SettingDescription>
                    {t('pages.user.profile.fields.auth_type.description')}
                  </SettingDescription>
                </SettingLabelWrapper>
                <SettingInputWrapper>
                  <DetailTextField disabled fullWidth value={currentUser.authStrategy ?? ''} />
                </SettingInputWrapper>
              </SettingsData>

              {(!currentUser.authStrategy || currentUser.authStrategy === 'FANCONNECT') && (
                <>
                  <SettingsData>
                    <SettingLabelWrapper>
                      <SettingName>
                        {t('pages.user.profile.fields.current_password.label')}
                      </SettingName>
                      <SettingDescription>
                        {t('pages.user.profile.fields.current_password.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailPasswordField
                        disabled={formik.isSubmitting}
                        error={!!formik.errors.oldPassword}
                        helperText={formik.errors.oldPassword}
                        kind="current"
                        name="oldPassword"
                        onChange={formik.handleChange}
                        sx={{ height: 'inherit', width: '100%' }}
                        value={formik.values.oldPassword}
                      />
                    </SettingInputWrapper>
                  </SettingsData>

                  <SettingsData>
                    <SettingLabelWrapper>
                      <SettingName>{t('pages.user.profile.fields.new_password.label')}</SettingName>
                      <SettingDescription>
                        {t('pages.user.profile.fields.new_password.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailPasswordField
                        disabled={formik.isSubmitting}
                        error={!!formik.errors.newPassword}
                        helperText={formik.errors.newPassword}
                        kind="new"
                        name="newPassword"
                        onChange={formik.handleChange}
                        sx={{ height: 'inherit', width: '100%', marginBottom: '12px !important' }}
                        value={formik.values.newPassword}
                      />
                      <PasswordStrengthMeterBox strength={passwordStrength} />
                    </SettingInputWrapper>
                  </SettingsData>

                  <SettingsData className="last">
                    <SettingLabelWrapper>
                      <SettingName>
                        {t('pages.user.profile.fields.verify_password.label')}
                      </SettingName>
                      <SettingDescription>
                        {t('pages.user.profile.fields.verify_password.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailPasswordField
                        disabled={formik.isSubmitting}
                        error={!!formik.errors.newPasswordConfirmation}
                        helperText={formik.errors.newPasswordConfirmation}
                        kind="new"
                        name="newPasswordConfirmation"
                        onChange={formik.handleChange}
                        sx={{ height: 'inherit', width: '100%', marginBottom: '12px !important' }}
                        value={formik.values.newPasswordConfirmation}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                </>
              )}
            </SettingsBody>
          </Box>
        </Box>
      </PageContainer>
    </>
  );
};
